import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectFilterValue } from '../store/slices/filterSliceV2';
import { cmsLayoutExclusions } from '../constants/Constants';
import {
  fetchLayoutStart,
  fetchLayoutSuccess,
  fetchLayoutFailure,
  setCurrentProductId,
  setCurrentModelId,
  selectCmsLayoutData,
  selectCmsLoading,
  selectCmsError,
  selectCmsCurrentProductId,
  selectCmsCurrentModelId,
  selectCmsTabs,
  selectCmsModels,
  selectHasCmsLayout,
  selectCmsProductLayouts,
  selectEffectiveGoLiveSourceConfig,
} from '../store/slices/cmsSlice';
import { fetchProductStructure } from '../services/cms/cmsLayoutService';
import { isCmsEnabled } from '../services/cms/datoCmsClient';

/**
 * Hook to manage CMS layout fetching based on selected product
 *
 * This hook:
 * 1. Listens to product changes in Redux filterSliceV2
 * 2. Checks cache before fetching - reuses cached data if available
 * 3. Fetches CMS layout when product changes and not cached
 * 4. Stores the result in Redux cmsSlice (cached by productId)
 *
 * @returns {Object} CMS layout state and utilities
 */
export const useCmsLayout = () => {
  const dispatch = useDispatch();

  // Get selected product ID from filterSliceV2 (now stores integer ID like 1, 2, etc.)
  const selectedProductId = useSelector((state) =>
    selectFilterValue(state, 'product')
  );

  // Get selected program ID from filterSliceV2
  const selectedProgramId = useSelector((state) =>
    selectFilterValue(state, 'program')
  );

  /**
   * Check if the current program or product is excluded from CMS layout checks
   */
  const isExcludedFromCms = useCallback(() => {
    // Check if program is excluded
    if (
      selectedProgramId != null &&
      cmsLayoutExclusions.programIds.includes(selectedProgramId)
    ) {
      return true;
    }
    // Check if product is excluded
    if (
      selectedProductId != null &&
      cmsLayoutExclusions.productIds.includes(selectedProductId)
    ) {
      return true;
    }
    return false;
  }, [selectedProgramId, selectedProductId]);

  // Get CMS state from Redux
  const productLayouts = useSelector(selectCmsProductLayouts);
  const layoutData = useSelector(selectCmsLayoutData);
  const loading = useSelector(selectCmsLoading);
  const error = useSelector(selectCmsError);
  const currentProductId = useSelector(selectCmsCurrentProductId);
  const currentModelId = useSelector(selectCmsCurrentModelId);
  const tabs = useSelector(selectCmsTabs);
  const models = useSelector(selectCmsModels);
  const hasCmsLayout = useSelector(selectHasCmsLayout);
  const goLiveSourceConfig = useSelector(selectEffectiveGoLiveSourceConfig);

  /**
   * Check if product layout is already cached
   */
  const isProductCached = useCallback(
    (productId) => {
      return productId in productLayouts;
    },
    [productLayouts]
  );

  /**
   * Fetch layout for a specific product (only if not cached)
   */
  const fetchLayout = useCallback(
    async (productId, forceRefresh = false) => {
      // Only fetch if CMS is enabled and productId is a valid non-null value
      if (!isCmsEnabled()) {
        return;
      }

      // Skip fetching if program or product is excluded from CMS
      if (isExcludedFromCms()) {
        return;
      }

      // Ensure productId is valid (not null, undefined, or empty)
      if (productId === null || productId === undefined || productId === '') {
        return;
      }

      // Check cache first - if already cached, just update currentProductId
      if (!forceRefresh && isProductCached(productId)) {
        dispatch(setCurrentProductId({ productId }));
        return;
      }

      dispatch(fetchLayoutStart({ productId }));

      try {
        const data = await fetchProductStructure(productId);

        dispatch(
          fetchLayoutSuccess({
            productId,
            layoutData: data, // Can be null if no CMS layout found
          })
        );
      } catch (err) {
        dispatch(
          fetchLayoutFailure({
            productId,
            error: err.message || 'Failed to fetch CMS layout',
          })
        );
      }
    },
    [dispatch, isProductCached]
  );

  // Update currentProductId immediately when product changes
  // This ensures LayoutV2 can detect the product change and show loader
  useEffect(() => {
    if (selectedProductId !== null && selectedProductId !== undefined) {
      // Always update currentProductId first (synchronously via dispatch)
      // This allows selectIsProductCmsStatusKnown to work correctly
      if (currentProductId !== selectedProductId) {
        dispatch(setCurrentProductId({ productId: selectedProductId }));
      }
    }
  }, [selectedProductId, currentProductId, dispatch]);

  // Fetch layout when product changes (separate effect to avoid race conditions)
  useEffect(() => {
    // Only fetch when we have a valid productId
    if (selectedProductId !== null && selectedProductId !== undefined) {
      fetchLayout(selectedProductId);
    }
  }, [selectedProductId, fetchLayout]);

  // Get selected model ID directly from Redux filterSliceV2
  const selectedModelId = useSelector((state) =>
    selectFilterValue(state, 'model')
  );

  // Sync selectedModelId from Redux filterSliceV2 to CMS Redux state
  // This ensures CMS components re-render when model changes (e.g., GS-SO model switching)
  useEffect(() => {
    if (selectedModelId !== undefined && selectedModelId !== currentModelId) {
      dispatch(setCurrentModelId({ modelId: selectedModelId }));
    }
  }, [selectedModelId, currentModelId, dispatch]);

  /**
   * Set the current model ID for model-based products
   */
  const setModelId = useCallback(
    (modelId) => {
      dispatch(setCurrentModelId({ modelId }));
    },
    [dispatch]
  );

  return {
    // State
    layoutData,
    loading,
    error,
    currentProductId,
    currentModelId,
    tabs,
    models,
    hasCmsLayout,
    selectedProductId,
    selectedProgramId,
    isExcludedFromCms: isExcludedFromCms(),
    goLiveSourceConfig,

    // Actions
    fetchLayout,
    setModelId,
    refetch: () => fetchLayout(selectedProductId),
  };
};

/**
 * Hook to get CMS tabs for the current product
 * Returns null if no CMS layout is available (fallback to registry)
 *
 * @returns {Array|null} Array of CMS tabs or null
 */
export const useCmsTabs = () => {
  const { tabs, loading, hasCmsLayout } = useCmsLayout();

  if (loading) {
    return { tabs: null, loading: true };
  }

  if (!hasCmsLayout) {
    return { tabs: null, loading: false };
  }

  return { tabs, loading: false };
};

export default useCmsLayout;
